<template>
  <div class="minimal-terms">
    <header class="term-header">
      <h1 class="main-heading">{{ appName }} User Service Agreement</h1>
    </header>

    <div class="intro-paragraph">
      Kindly read this entire Agreement before registering for an account or accessing our digital services. This document clearly defines the legal rights and obligations of both the platform and all users, while safeguarding all parties’ legitimate interests. All bolded and underlined clauses deserve your special attention to ensure full comprehension.
      You may not register, log in or utilize our software unless you accept every term herein. Any registration, login or service usage activity constitutes your full acknowledgment and legal acceptance of this Agreement.
      We retain the sole authority to revise, supplement or remove clauses of this Agreement, Privacy Policy and auxiliary rules at any time without prior user notification. If you disagree with updated provisions, you must terminate service access immediately. Continued platform use post-update equals your acceptance of revised terms.
    </div>

    <section class="term-section">
      <h2 class="section-label">1. Account Provisions</h2>
      <div class="section-content">
        1.1 You need to create a user account to access our services; we reserve rights to adjust the registration process at will.
        <br /><br />
        1.2 Account registration authorizes us to activate accounts for other products operated by our group simultaneously, subject to actual business arrangements.
        <br /><br />
        1.3 All accounts remain our proprietary property; users only obtain limited usage permissions. Account gifting, lending, renting, transfer and resale are strictly forbidden.
        <br /><br />
        1.4 You bear exclusive responsibility for account and password security. Any account leakage or unauthorized access arising from your negligence shall be your sole liability. All operations linked to your account are deemed your personal conduct. Notify us instantly upon suspected account intrusion; using others’ login credentials is prohibited in all scenarios.
        <br /><br />
        1.5 Violation of any Agreement clause grants us authority to suspend or permanently terminate your service access.
        <br /><br />
        1.6 Accounts dormant for 12 consecutive months may be deleted or reclaimed without advance notice for resource optimization. We also reserve discretionary rights to retrieve any account and username at any time.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">2. Service Boundaries</h2>
      <div class="section-content">
        2.1 Available software functions include video chat, in-app purchases and virtual gift delivery, subject to real-time operational deployment. We may alter service modules without prior notification.
        <br /><br />
        2.2 You consent to receive commercial promotions and official service notifications via email, app push, SMS and web pages. Mobile network characteristics entitle us to collect basic device information for normal service operation.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">3. User Data Protection</h2>
      <div class="section-content">
        3.1 You must submit legally required identity materials during registration. Incomplete, false or non-compliant information will result in restricted or denied service access.
        <br /><br />
        3.2 To upgrade service experience and technical capabilities, we may collect, utilize and share your authorized data with qualified third parties. Refer to our independent Privacy Policy for detailed regulations.
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">4. Content Publishing Standards</h2>
      <div class="section-content">
        4.1 "Content" covers all materials you generate, upload, copy and publish, including avatars, usernames, personal bios, text, audio, visuals, videos, messages and hyperlinks.
        <br /><br />
        4.2 The following content and behaviors are prohibited on our software platform:
        <br /><br />
        1. Material threatening national security, leaking state secrets or damaging national unity;
        <br /><br />
        2. Content that undermines national honor and legal national interests;
        <br /><br />
        3. Statements inciting ethnic hatred, discrimination or communal division;
        <br /><br />
        4. Content violating national religious policies or promoting cults and feudal superstition;
        <br /><br />
        5. Rumors and information disrupting public order and social stability;
        <br /><br />
        6. Obscene, pornographic and sexually suggestive media;
        <br /><br />
        7. Content advocating gambling, violent crime and terrorist acts;
        <br /><br />
        8. Insulting, defamatory, fraudulent material infringing third-party legal rights;
        <br /><br />
        9. Communications containing abusive, threatening language;
        <br /><br />
        10. Harassment spam, malicious scripts and phishing links;
        <br /><br />
        11. Material disclosing others’ private information;
        <br /><br />
        12. Works violating reputation, portrait, intellectual property and trade secret rights;
        <br /><br />
        13. Any content harming minors’ legitimate interests;
        <br /><br />
        14. Stalking and persistent harassment of other users;
        <br /><br />
        15. False information and identity impersonation via stolen profile data;
        <br /><br />
        16. Coercion or inducement to follow accounts, click links or share content;
        <br /><br />
        17. Fabricated facts intended to mislead other users;
        <br /><br />
        18. Unauthorized commercial advertising and sales postings;
        <br /><br />
        19. Mass fake account creation through technical tools;
        <br /><br />
        20. Removal of built-in software copyright marks;
        <br /><br />
        21. Reverse engineering, decompilation and source code cracking;
        <br /><br />
        22. Unauthorized copying, mirror site building and commercial use of our intellectual property;
        <br /><br />
        23. Tampering and derivative creation of official platform data;
        <br /><br />
        24. Modifying software logic and redistributing altered programs;
        <br /><br />
        25. Login via unauthorized plugins, hacks and third-party tools;
        <br /><br />
        26. Vulgar, offensive and distasteful public content;
        <br /><br />
        27. All other acts violating laws, infringing third-party rights or disrupting platform operation. We complete content review within 24 hours; illegal content will be erased, and severe violations lead to permanent account suspension.
        <br /><br />
        4.3 All user content solely reflects personal opinions and does not represent platform stances. We assume zero liability for user-generated content and individual behaviors.
        <br /><br />
        4.4 You take full legal responsibility for all published material’s authenticity and legality. Any losses incurred to us or third parties due to your misconduct shall be fully compensated by you.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">5. Third-Party Goods & Services</h2>
      <div class="section-content">
        5.1 The platform displays third-party advertisements; you consent to view such content during usage. All advertisement-based transactions are undertaken at your own risk, and we disclaim related liability unless mandatory laws require otherwise.
        <br /><br />
        5.2 External linked third-party products require compliance with their independent terms. We offer no guarantee for third-party service safety and bear no losses arising from their use.
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">6. Membership Services</h2>
      <div class="section-content">
        6.1 Paid membership packages are available, with privileges and pricing displayed on official service pages.
        <br /><br />
        6.2 We reserve rights to adjust membership fees and service contents at any time via page updates.
        <br /><br />
        6.3 Membership payments are non-refundable and non-transferable unless legally mandated. Membership expires automatically upon term end; unused duration cannot be extended.
        <br /><br />
        6.4 Membership registration data must remain accurate and updated; outdated information preventing privilege usage is your sole responsibility.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">7. Subscriptions & Virtual Assets</h2>
      <div class="section-content">
        7.1 Subscription Plans
        Monthly, bimonthly and quarterly subscriptions are offered. Mobile orders will charge your iTunes or Google account and renew automatically. Disable auto-renewal 24 hours prior to expiration to avoid recurring fees. Active subscriptions cannot be mid-term refunded, yet auto-renewal can be turned off anytime. All subscription fees are non-refundable and non-transferable, with no cash conversion allowed. We may adjust pricing or discontinue subscription plans freely.
        <br /><br />
        7.2 Virtual Currency
        Virtual items such as Diamonds are our exclusive property and only grant limited usage rights for stories, filters and video chat. We may verify your eligibility to utilize virtual assets. No refunds or compensations will be issued for virtual currency due to service maintenance, suspension or system failures. Virtual currency cannot be traded, refunded or exchanged for cash; all unused balances are forfeited without compensation upon account termination.
        <br /><br />
        7.3 Payment Channels
        Paid functions can be purchased through Apple and Google accounts, with all charges shown on third-party billing records. You must abide by each payment platform’s independent service rules.
        <br /><br />
        7.4 Cancellation Rules
        Subscriptions purchased via third parties must be canceled within the corresponding platform. Benefits remain valid until the current billing cycle concludes, with no partial refunds issued.
        <br /><br />
        7.5 Term & Charges
        This Agreement remains effective during all platform usage. We may revoke your membership without notice or fee refund. Mobile SMS and data fees shall be borne by you as the user.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">8. Data Storage Rules</h2>
      <div class="section-content">
        8.1 We take no accountability for data loss or accidental deletion within our platform.
        <br /><br />
        8.2 We set data storage duration and capacity limits based on operational needs; regular local backups of vital information are recommended.
        <br /><br />
        8.3 Upon service termination or account abandonment, we may permanently erase your server data without obligation to return copies.
        <br /><br />
        8.4 You retain rights to edit and delete personal content, while accepting all risks of associated data removal.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">9. Risk Disclosure</h2>
      <div class="section-content">
        9.1 This software acts merely as an information exchange medium. All account operations and their resulting consequences fall under your full liability. You independently judge all platform content and bear all usage risks.
        <br /><br />
        9.2 Report any violations or improper user conduct to us immediately for official disposal.
        <br /><br />
        9.3 We retain full discretion to adjust, suspend or discontinue any platform functions without advance notification.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">10. Intellectual Property</h2>
      <div class="section-content">
        10.1 All platform intellectual property belongs to us, excluding original advertiser content. User pre-existing content IP remains under your ownership.
        <br /><br />
        10.2 All software copyrights and patent rights are held exclusively by our company.
        <br /><br />
        10.3 Platform logos, graphics and product names are protected by trademark and copyright law; written consent is required for all external display or modification.
        <br /><br />
        10.4 Unauthorized reproduction and derivative creation of our intellectual property is prohibited by law.
        <br /><br />
        10.5 You retain IP rights over uploaded text, images and videos, while granting us a permanent, worldwide, royalty-free exclusive license to edit, distribute and sublicense your content for commercial and non-commercial purposes.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">11. Liability Regulations</h2>
      <div class="section-content">
        11.1 For illegal or breach-of-contract conduct, we may issue warnings, restrict functions, blacklist accounts, deduct virtual assets and publish violation records. Terminated accounts receive no virtual asset refunds.
        <br /><br />
        11.2 We reserve rights to submit violation records to regulatory bodies and initiate legal action, with all resulting consequences borne by you.
        <br /><br />
        11.3 You shall cover all third-party compensation claims and associated legal fees arising from your misconduct.
        <br /><br />
        11.4 You agree to indemnify our company and affiliates against all losses, lawsuits and expenses caused by your service usage or Agreement breaches.
        <br /><br />
        11.5 We may implement the above penalties individually or simultaneously.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">12. Force Majeure & Liability Exclusions</h2>
      <div class="section-content">
        12.1 Service interruptions triggered by natural disasters, warfare, riots and government measures are not our liability, though we will attempt timely restoration.
        <br /><br />
        12.2 We offer no warranty of uninterrupted, error-free service. Within legal limits, all express and implied service guarantees are disclaimed.
        <br /><br />
        12.3 Network instability, viruses and external software create security hazards; secure your account credentials independently.
        <br /><br />
        12.4 Losses from hacking, system crashes and telecom failures are not compensable by our platform.
        <br /><br />
        12.5 We bear no responsibility for misleading, defamatory or illegal content posted by other users.
        <br /><br />
        12.6 Scheduled maintenance with prior notice will not incur liability for temporary service downtime.
        <br /><br />
        12.7 We have the right, not the obligation, to detect and resolve third-party violations.
        <br /><br />
        12.8 Free promotional products carry no quality guarantee from our side.
        <br /><br />
        12.9 Indirect, consequential damages including lost profits are excluded from compensation; total payout caps equal all fees you have paid us.
        <br /><br />
        12.10 Offline meetings and transactions between users carry independent personal and property risks, with no platform liability.
        <br /><br />
      </div>
    </section>

    <section class="term-section">
      <h2 class="section-label">13. General Clauses</h2>
      <div class="section-content">
        13.1 Carefully review disclaimer and risk clauses. Minors must read alongside legal guardians.
        <br /><br />
        13.2 All in-app official notices constitute supplementary terms with identical legal weight.
        <br /><br />
        13.3 This Agreement is governed by Hong Kong SAR laws, with all disputes arbitrated at the Hong Kong International Arbitration Centre.
        <br /><br />
        13.4 Invalid individual clauses do not nullify the rest of this Agreement. Parties will negotiate substitute provisions to achieve original business intent.
      </div>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      appName: ""
    }
  },
  created() {}
}
</script>

<style scoped>
/* 简约高级全局配置：极致留白、低饱和灰蓝、无厚重阴影、纤细无衬线英文字体 */
.minimal-terms {
  max-width: 840px;
  margin: 64px auto;
  padding: 0 32px;
  background: #ffffff;
  font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #222222;
}

/* 头部标题 */
.term-header {
  margin-bottom: 48px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e6e8ec;
}
.main-heading {
  font-size: 26px;
  font-weight: 500;
  text-align: center;
  margin: 0;
  color: #161a23;
  letter-spacing: 0.6px;
}

/* 开篇引言 */
.intro-paragraph {
  font-size: 15px;
  line-height: 1.85;
  color: #333844;
  margin-bottom: 40px;
}

/* 章节容器 */
.term-section {
  margin-bottom: 36px;
}

/* 章节标题 极简细蓝标识 */
.section-label {
  font-size: 17px;
  font-weight: 500;
  color: #274b82;
  margin: 0 0 14px 0;
  padding-left: 6px;
  border-left: 2px solid #274b82;
}

/* 正文文本 */
.section-content {
  font-size: 14.5px;
  line-height: 1.8;
  color: #444955;
}

/* 移动端适配 缩小留白 */
@media (max-width: 768px) {
  .minimal-terms {
    margin: 32px auto;
    padding: 0 16px;
  }
  .main-heading {
    font-size: 22px;
  }
  .intro-paragraph, .section-content {
    font-size: 14px;
  }
  .section-label {
    font-size: 16px;
  }
}
</style>